import { useCallback, useEffect, useState } from "react";

const STORAGE_KEY = "cp_recently_viewed_v1";
const MAX_ITEMS = 6;

export interface RecentItem {
  id: string;
  slug: string;
  title: string;
  type: string;
  thumbnailUrl: string | null;
  sourceAuthor: string | null;
  publishedAt: string | null;
  /** UNIX millis when the user opened this — newest sorts first. */
  viewedAt: number;
}

function read(): RecentItem[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    // Defensive: drop entries missing the fields we render so a stale schema
    // doesn't crash the strip.
    return parsed.filter(
      (it): it is RecentItem =>
        !!it &&
        typeof it === "object" &&
        typeof (it as RecentItem).slug === "string" &&
        typeof (it as RecentItem).title === "string" &&
        typeof (it as RecentItem).viewedAt === "number",
    );
  } catch {
    return [];
  }
}

function write(items: RecentItem[]) {
  if (typeof window === "undefined") return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  } catch {
    // Storage full, blocked, etc — best-effort, never crash the app.
  }
}

/**
 * Same-tab pub/sub so the Home strip refreshes immediately when the user
 * opens an item from the lightbox or detail page (since the storage event
 * doesn't fire in the tab that wrote it).
 */
const listeners = new Set<() => void>();
function notifyListeners() {
  for (const fn of listeners) fn();
}

export function useRecentlyViewed() {
  const [items, setItems] = useState<RecentItem[]>(() => read().slice(0, MAX_ITEMS));

  useEffect(() => {
    const onChange = () => setItems(read().slice(0, MAX_ITEMS));
    listeners.add(onChange);
    // Cross-tab refresh — when another tab writes, this fires and we re-read.
    window.addEventListener("storage", onChange);
    return () => {
      listeners.delete(onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  const track = useCallback(
    (item: Omit<RecentItem, "viewedAt">) => {
      const now = Date.now();
      const next: RecentItem = { ...item, viewedAt: now };
      const merged = [next, ...read().filter((it) => it.slug !== item.slug)].slice(0, MAX_ITEMS);
      write(merged);
      notifyListeners();
    },
    [],
  );

  const clear = useCallback(() => {
    write([]);
    notifyListeners();
  }, []);

  return { items, track, clear };
}
